import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatTime, plural } from '@rental/i18n'
import { type RefObject, useId, useLayoutEffect, useRef, useState } from 'react'
import { useLocale } from '../i18n'
import { clockTime, type DayLineModel, dayLine } from './day-line'

/** The drawing's height and its axis, in pixels. */
const HEIGHT = 96
const AXIS = 48
/** The first and last marks keep clear of the pane's edges. */
const EDGE = 24
/** The least room an hour's label takes, so two labels never touch. */
const LABEL_ROOM = 64
/** The "now" label. */
const NOW_WIDTH = 56
const NOW_HEIGHT = 20

/**
 * UI-1c: the day at the port, from its first moment to its last. Pick-ups
 * stand above the line in blue and returns below it in graphite, the
 * customers' ferries sit on it, and now is a navy line under a gold label:
 * gold marks now and nothing else (DESIGN.md, "Colour"). The lists below say
 * all of it again, row by row, so the drawing is one image with a sentence
 * for a screen reader.
 */
export function DayLine({ board }: { board: ApiSchemas['TodayResponse'] }) {
  const model = dayLine(board)
  return model ? <DayLineGroup model={model} now={board.now} /> : null
}

function DayLineGroup({ model, now }: { model: DayLineModel; now: string }) {
  const { locale, messages: m } = useLocale()
  const t = m.today.dayLine
  const id = useId()
  const box = useRef<HTMLDivElement>(null)
  const width = useWidth(box)
  const items = [
    plural(locale, t.pickups, model.counts.pickups),
    plural(locale, t.returns, model.counts.returns),
    ...(model.counts.ferries > 0 ? [plural(locale, t.ferries, model.counts.ferries)] : []),
  ].join(', ')
  const summary = [
    format(t.summary, { from: clockTime(model.from), to: clockTime(model.to), items }),
    ...(model.now === null ? [] : [format(t.nowAt, { time: formatTime(locale, now) })]),
  ].join(' ')
  return (
    <section
      aria-labelledby={id}
      className="glass-pane flex flex-col gap-3 rounded-card p-4 sm:p-6"
    >
      <h2 id={id} className="text-xl">
        {t.title}
      </h2>
      <div ref={box} className="h-24">
        {width > 0 ? (
          <Drawing
            model={model}
            width={width}
            summary={summary}
            nowLabel={formatTime(locale, now)}
          />
        ) : null}
      </div>
      <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <Legend shape="pickup">{t.legendPickups}</Legend>
        <Legend shape="return">{t.legendReturns}</Legend>
        {model.counts.ferries > 0 ? <Legend shape="ferry">{t.legendFerries}</Legend> : null}
        {model.now === null ? null : <Legend shape="now">{t.legendNow}</Legend>}
      </ul>
    </section>
  )
}

function Drawing({
  model,
  width,
  summary,
  nowLabel,
}: {
  model: DayLineModel
  width: number
  summary: string
  nowLabel: string
}) {
  const span = model.to - model.from
  const plot = width - 2 * EDGE
  const x = (minute: number) => EDGE + ((minute - model.from) / span) * plot
  // Every hour when there is room, else every second or third: never a crowd.
  const every = Math.max(1, Math.ceil(LABEL_ROOM / (plot / (span / 60))))
  const hours: number[] = []
  for (let minute = model.from; minute <= model.to; minute += every * 60) hours.push(minute)
  return (
    <svg
      role="img"
      aria-label={summary}
      width={width}
      height={HEIGHT}
      viewBox={`0 0 ${width} ${HEIGHT}`}
      // Measured after a resize, so for a frame it keeps the old width: held to its box
      // meanwhile, so a turned iPad never finds the page wider than the screen.
      className="block max-w-full"
    >
      <line
        x1={EDGE}
        x2={width - EDGE}
        y1={AXIS}
        y2={AXIS}
        strokeWidth={2}
        className="stroke-rule"
      />
      {hours.map((minute) => (
        <g key={minute}>
          <line
            x1={x(minute)}
            x2={x(minute)}
            y1={AXIS - 4}
            y2={AXIS + 4}
            strokeWidth={2}
            className="stroke-rule"
          />
          <text
            x={x(minute)}
            y={HEIGHT - 4}
            textAnchor="middle"
            className="figures fill-text-muted text-sm"
          >
            {clockTime(minute)}
          </text>
        </g>
      ))}
      {model.marks.map((mark, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: two marks may share a booking and a minute
        <Mark key={index} kind={mark.kind} x={x(mark.minute)} />
      ))}
      {model.now === null ? null : <Now x={x(model.now)} width={width} label={nowLabel} />}
    </svg>
  )
}

function Mark({ kind, x }: { kind: 'pickup' | 'return' | 'ferry'; x: number }) {
  if (kind === 'ferry') {
    return (
      <rect
        x={x - 5}
        y={AXIS - 5}
        width={10}
        height={10}
        transform={`rotate(45 ${x} ${AXIS})`}
        className="fill-text-muted"
      />
    )
  }
  return (
    <rect
      x={x - 2}
      y={kind === 'pickup' ? AXIS - 22 : AXIS + 4}
      width={4}
      height={18}
      rx={2}
      className={kind === 'pickup' ? 'fill-brand' : 'fill-text'}
    />
  )
}

function Now({ x, width, label }: { x: number; width: number; label: string }) {
  const left = Math.min(Math.max(x - NOW_WIDTH / 2, 0), width - NOW_WIDTH)
  return (
    <g>
      <line
        x1={x}
        x2={x}
        y1={NOW_HEIGHT}
        y2={AXIS + 26}
        strokeWidth={2}
        className="stroke-brand-strong"
      />
      <rect
        x={left}
        y={0}
        width={NOW_WIDTH}
        height={NOW_HEIGHT}
        rx={NOW_HEIGHT / 2}
        className="fill-mz-gold-500"
      />
      <text
        x={left + NOW_WIDTH / 2}
        y={15}
        textAnchor="middle"
        className="figures fill-brand-strong text-sm font-bold"
      >
        {label}
      </text>
    </g>
  )
}

function Legend({
  shape,
  children,
}: {
  shape: 'pickup' | 'return' | 'ferry' | 'now'
  children: string
}) {
  return (
    <li className="flex items-center gap-2">
      <svg aria-hidden="true" width={16} height={16} viewBox="0 0 16 16">
        {shape === 'pickup' ? (
          <rect x={6} y={0} width={4} height={12} rx={2} className="fill-brand" />
        ) : null}
        {shape === 'return' ? (
          <rect x={6} y={4} width={4} height={12} rx={2} className="fill-text" />
        ) : null}
        {shape === 'ferry' ? (
          <rect
            x={4}
            y={4}
            width={8}
            height={8}
            transform="rotate(45 8 8)"
            className="fill-text-muted"
          />
        ) : null}
        {shape === 'now' ? (
          <rect x={0} y={4} width={16} height={8} rx={4} className="fill-mz-gold-500" />
        ) : null}
      </svg>
      {children}
    </li>
  )
}

/** The element's width in pixels, kept up to date, so the drawing's text keeps its true size. */
function useWidth(element: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const box = element.current
    if (!box) return
    const measure = () => setWidth(Math.floor(box.getBoundingClientRect().width))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [element])
  return width
}
